import React, {
  createContext,
  Fragment,
  useContext,
  useEffect,
  useState,
} from "react";
import makeStyles from "@mui/styles/makeStyles";
import { MediaContext } from "../../common/media-context";
import {
  useBadges,
  useLinkBadgeToContent,
} from "../../../manage-badges/badgesServices";
import {
  any,
  concat,
  differenceWith,
  equals,
  isEmpty,
  map,
  propEq,
  reject,
} from "ramda";
import { deduplicate } from "../../../util/mediaUtil";
import { useSnackAlert } from "../../../common/use-snackAlert";

import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

import BadgesTable from "../../../manage-badges/BadgesTable";
import MediaDialog from "../../common/MediaDialog";
import { notBlank } from "../../../util/ext";

const BadgesContext = createContext({
  selected: [],
  setSelected: () => {},
  error: "",
  setError: () => {},
});

const useStyles = makeStyles(theme => ({
  selected: {
    color: "black",
    fontWeight: 700,
  },

  selectAll: {
    marginLeft: 10,
  },
}));

const SelectAllCheckbox = ({ badges }) => {
  const context = useContext(BadgesContext);
  const [checkbox, setCheckbox] = useState({
    checked: false,
    indeterminate: false,
  });

  useEffect(() => {
    if (!isEmpty(context.selected)) {
      setCheckbox({
        checked: equals(context.selected.length, badges.length),
        indeterminate: !equals(context.selected.length, badges.length),
      });
    } else {
      setCheckbox({ checked: false, indeterminate: false });
    }
  }, [context.selected, badges]);

  const handleChange = event => {
    context.setError("");
    context.setSelected(prev => {
      if (checkbox.checked && !isEmpty(context.selected)) {
        return differenceWith(
          (prev, badges) => prev.id === badges.id,
          prev,
          badges,
        );
      } else if (checkbox.checked) {
        return [];
      } else {
        return deduplicate(concat(context.selected, badges));
      }
    });
  };
  return (
    <Checkbox
      color="primary"
      checked={checkbox.checked}
      indeterminate={checkbox.indeterminate}
      onChange={handleChange}
    />
  );
};

const BadgeCheckbox = ({ badge }) => {
  const context = useContext(BadgesContext);

  const handleChange = () => {
    context.setError("");
    context.setSelected(prev => {
      const hasBadge = any(propEq("id", badge.id), prev);
      const updated = hasBadge
        ? reject(propEq("id", badge.id), prev)
        : deduplicate(concat(prev, [badge]));

      return updated;
    });
  };

  return (
    <Checkbox
      color="primary"
      onChange={handleChange}
      checked={any(propEq("id", badge.id), context.selected)}
    />
  );
};

const errMsg = "Please select a badge";

const BadgesDialog = ({ onCancel, visible }) => {
  const classes = useStyles();
  const context = useContext(MediaContext);
  const badgesResults = useBadges();
  const { SnackAlert, showAlert } = useSnackAlert();

  const [selectedBadges, setSelectedBadges] = useState([]);
  const [error, setError] = useState("");

  const handleClose = () => {
    onCancel();
    setError("");
    setSelectedBadges([]);
  };

  const linkBadgeToContent = useLinkBadgeToContent();
  const handleSave = async () => {
    try {
      if (isEmpty(selectedBadges)) {
        setError(errMsg);
        throw new Error(errMsg);
      }

      await Promise.all(
        map(
          badge =>
            linkBadgeToContent.mutateAsync({
              resourceIds: {
                mediaId: context.media.id,
                badgeId: badge.id,
              },
            }),
          selectedBadges,
        ),
      );

      handleClose();
    } catch (err) {
      showAlert("error", [err.message]);
    }
  };

  return (
    <BadgesContext.Provider
      value={{
        selected: selectedBadges,
        setSelected: setSelectedBadges,
        error,
        setError,
      }}
    >
      <MediaDialog
        open={visible}
        title={
          <Fragment>
            Add Badges{" "}
            {!isEmpty(selectedBadges) && badgesResults.isSuccess && (
              <FormControlLabel
                classes={{ root: classes.selectAll }}
                label={
                  <span className={classes.selected}>
                    {selectedBadges.length} Selected
                  </span>
                }
                control={<SelectAllCheckbox badges={badgesResults.data} />}
              />
            )}
          </Fragment>
        }
        maxWidth="lg"
        handleClose={handleClose}
        content={
          <Fragment>
            <SnackAlert />
            {notBlank(error) && <p style={{ color: "red" }}>{error}</p>}
            <BadgesTable
              type="all"
              SelectAllCheckbox={SelectAllCheckbox}
              BadgeCheckbox={BadgeCheckbox}
              showCheckbox
              showEdit={false}
            />
          </Fragment>
        }
        actions={
          <Fragment>
            <Button
              disabled={linkBadgeToContent.isPending}
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              disabled={linkBadgeToContent.isPending}
              variant="contained"
              color="primary"
              onClick={handleSave}
              style={{ width: "auto" }}
            >
              Add Badges
            </Button>
          </Fragment>
        }
      />
    </BadgesContext.Provider>
  );
};

export default BadgesDialog;
